<template>
    <div class="playing-details" :style="backgroundStyle">
        <div class="playing-details__ambient" aria-hidden="true"></div>
        <div class="playing-details__shell">
            <section class="playing-details__art-panel">
                <div class="playing-details__meta-top">
                    <div>
                        <p class="playing-details__label">{{ $tt('正在播放') }}</p>
                        <h1 class="playing-details__title">{{ displayTitle }}</h1>
                    </div>
                    <div class="playing-details__time-pill">
                        {{ formatDuration(currentTime) }} / {{ formatDuration(duration) }}
                    </div>
                </div>

                <div :class="['playing-details__art-stage', isPlaying ? 'is-playing' : '']">
                    <div class="playing-details__art-glow" aria-hidden="true"></div>
                    <div class="playing-details__record" aria-hidden="true">
                        <div class="playing-details__record-label"></div>
                    </div>
                    <div class="playing-details__art-card">
                        <img
                            :src="songImageWall"
                            :alt="displayTitle"
                            class="playing-details__cover"
                        >
                    </div>
                    <div class="playing-details__tonearm" aria-hidden="true"></div>
                </div>

                <div class="playing-details__track-card">
                    <div class="playing-details__track-copy">
                        <span>{{ $tt('艺术家') }}</span>
                        <strong>{{ displayArtist }}</strong>
                    </div>
                    <div class="playing-details__track-copy">
                        <span>{{ $tt('专辑') }}</span>
                        <strong>{{ displayAlbum }}</strong>
                    </div>
                </div>
            </section>

            <section class="playing-details__lyrics-panel">
                <div class="playing-details__lyrics-frame">
                    <div class="playing-details__lyrics-fade playing-details__lyrics-fade--top" aria-hidden="true"></div>
                    <div class="playing-details__lyrics-fade playing-details__lyrics-fade--bottom" aria-hidden="true"></div>
                    <div ref="lyricContainer" class="playing-details__lyrics-scroll">
                        <div class="playing-details__lyrics-spacer"></div>

                        <div v-if="isLoading || lyricsLoading" class="playing-details__lyrics-loading">
                            <span v-for="item in 7" :key="item"></span>
                        </div>

                        <template v-else-if="lyricsObjArr.length">
                            <button
                                v-for="(item, i) in lyricsObjArr"
                                :key="item.uid"
                                type="button"
                                :data-lyric-index="i"
                                :class="lyricLineClass(i)"
                                @click="scrollToLyric(i)"
                            >
                                {{ item.lyric }}
                            </button>
                        </template>

                        <div v-else class="playing-details__lyrics-empty">
                            <strong>{{ $tt('暂无歌词') }}</strong>
                            <span>{{ $tt('可以先专注封面和当前播放信息') }}</span>
                        </div>

                        <div class="playing-details__lyrics-spacer"></div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    name: 'playing-list',
    data() {
        return {
            id: this.$route.params.id,
            song: null,
            lyricIndex: -1,
            lyricsObjArr: [],
            isLoading: false,
            lyricsLoading: false
        }
    },
    computed: {
        ...mapGetters(['currentTime', 'trackId', 'duration']),
        coverImage() {
            return (this.song && this.song.image) || '/static/dist/img/album_null.jpg'
        },
        backgroundStyle() {
            return {
                '--playing-cover': `url('${this.coverImage}')`
            }
        },
        songImageWall() {
            if (this.song && this.song.image) {
                return this.song.image.replace('al-', 'alw-')
            }
            return this.coverImage
        },
        displayTitle() {
            return (this.song && this.song.title) || this.$tt('载入中')
        },
        displayArtist() {
            return (this.song && this.song.artist) || this.$tt('未知艺术家')
        },
        displayAlbum() {
            return (this.song && this.song.album) || this.$tt('未知专辑')
        },
        isPlaying() {
            return this.$store.state.common.isPlaying
        },
        activeLyric() {
            if (this.lyricIndex < 0 || !this.lyricsObjArr[this.lyricIndex]) return ''
            return this.lyricsObjArr[this.lyricIndex].lyric
        }
    },
    watch: {
        currentTime: {
            handler(value) {
                this.syncCurrentLyric(value)
            },
            immediate: true
        },
        trackId(value) {
            if (this.$route.name !== 'playingList') return
            if (!value || String(value) === String(this.id)) return
            this.loadTrack(value)
        },
        '$route.params.id'(value) {
            if (this.$route.name !== 'playingList') return
            if (!value || String(value) === String(this.id)) return
            this.loadTrack(value)
        }
    },
    created() {
        if (this.$route.name !== 'playingList') return
        this.loadTrack(this.id)
    },
    methods: {
        async loadTrack(id) {
            if (!id) return
            this.id = id
            this.isLoading = true
            this.lyricsObjArr = []
            this.lyricIndex = -1

            try {
                this.song = await this.$api.subsonic.getSong(id)
                await this.fetchLyrics(id)
            } catch (error) {
                this.song = null
                this.lyricsObjArr = []
                this.lyricIndex = -1
            } finally {
                this.isLoading = false
                this.$nextTick(() => {
                    this.syncCurrentLyric(this.currentTime)
                })
            }
        },
        async fetchLyrics(id) {
            this.lyricsLoading = true
            try {
                const res = await this.$api.subsonic.getLyrics(id)
                this.lyricsObjArr = this.formatLyrics(res && res.value)
            } catch (error) {
                this.lyricsObjArr = []
            } finally {
                this.lyricsLoading = false
            }
        },
        formatLyrics(lyrics) {
            if (!lyrics) return []
            const lyricsObjArr = []
            lyrics.split(/\r?\n/).forEach((line, lineIndex) => {
                if (!line) return
                const timeTags = line.match(/\[[^\]]+\]/g)
                const content = line.replace(/\[[^\]]+\]/g, '').trim()
                if (!timeTags || !content) return

                timeTags.forEach((tag, tagIndex) => {
                    const time = this.formatLyricTime(tag.slice(1, -1))
                    if (!Number.isFinite(time)) return
                    lyricsObjArr.push({
                        lyric: content,
                        time,
                        uid: `${time}-${lineIndex}-${tagIndex}`
                    })
                })
            })
            return lyricsObjArr.sort((a, b) => a.time - b.time)
        },
        formatLyricTime(value) {
            const parts = String(value).split(':')
            const last = parseFloat(parts.pop())
            if (!Number.isFinite(last)) return NaN

            let total = last
            let multiplier = 60
            while (parts.length) {
                const part = parseInt(parts.pop(), 10)
                if (!Number.isFinite(part)) return NaN
                total += part * multiplier
                multiplier *= 60
            }
            return total
        },
        syncCurrentLyric(value) {
            if (!this.lyricsObjArr.length) {
                this.lyricIndex = -1
                return
            }

            const current = Number(value) || 0
            let activeIndex = -1
            for (let i = 0; i < this.lyricsObjArr.length; i += 1) {
                if (current >= parseFloat(this.lyricsObjArr[i].time)) {
                    activeIndex = i
                } else {
                    break
                }
            }

            if (activeIndex !== this.lyricIndex) {
                this.lyricIndex = activeIndex
                this.scrollActiveLyricIntoView(activeIndex)
            }
        },
        scrollActiveLyricIntoView(index) {
            if (index < 0) return
            this.$nextTick(() => {
                const container = this.$refs.lyricContainer
                if (!container) return
                const target = container.querySelector(`[data-lyric-index="${index}"]`)
                if (!target) return

                const targetCenter = target.offsetTop + target.offsetHeight / 2
                const desiredTop = targetCenter - container.clientHeight / 2
                const maxTop = Math.max(0, container.scrollHeight - container.clientHeight)
                container.scrollTo({
                    top: Math.max(0, Math.min(desiredTop, maxTop)),
                    behavior: 'smooth'
                })
            })
        },
        lyricLineClass(index) {
            const distance = Math.abs(index - this.lyricIndex)
            return [
                'playing-details__lyric-line',
                distance === 0 ? 'is-active' : '',
                distance === 1 ? 'is-near' : '',
                distance === 2 ? 'is-mid' : '',
                distance >= 3 ? 'is-far' : ''
            ]
        },
        scrollToLyric(index) {
            const line = this.lyricsObjArr[index]
            if (!line || !this.duration) return
            const seek = Math.min((parseFloat(line.time) || 0) / this.duration, 1)
            this.$store.dispatch('seek', seek)
        },
        formatDuration(value) {
            const safeValue = Number(value) || 0
            const minutes = Math.floor(safeValue / 60)
            const seconds = Math.floor(safeValue % 60)
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        }
    }
}
</script>

<style scoped>
.playing-details {
    position: relative;
    height: calc(100dvh - 85px);
    min-height: 36rem;
    overflow: hidden;
    border-radius: 1rem;
    color: oklch(var(--bc));
    background:
        radial-gradient(circle at 18% 12%, color-mix(in oklch, oklch(var(--p)) 16%, transparent), transparent 24rem),
        radial-gradient(circle at 84% 18%, color-mix(in oklch, oklch(var(--bc)) 8%, transparent), transparent 26rem),
        linear-gradient(135deg, oklch(var(--b2)), oklch(var(--b1)));
}

.playing-details__ambient {
    position: absolute;
    inset: -18%;
    background-image: var(--playing-cover);
    background-size: cover;
    background-position: center;
    opacity: 0.16;
    filter: blur(48px) saturate(1.12);
    transform: scale(1.05);
}

.playing-details__ambient::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
        linear-gradient(90deg, oklch(var(--b1) / 0.92), oklch(var(--b1) / 0.68)),
        linear-gradient(180deg, transparent, oklch(var(--b1)));
}

.playing-details__shell {
    position: relative;
    z-index: 1;
    height: 100%;
    max-width: 92rem;
    margin: 0 auto;
    padding: clamp(1rem, 2.5vw, 2rem);
    display: grid;
    grid-template-columns: minmax(20rem, 0.92fr) minmax(0, 1.08fr);
    gap: clamp(1rem, 2vw, 1.5rem);
}

.playing-details__art-panel,
.playing-details__lyrics-panel {
    min-width: 0;
    min-height: 0;
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 10%, transparent);
    border-radius: 1.5rem;
    background:
        linear-gradient(135deg, color-mix(in oklch, oklch(var(--b1)) 90%, transparent), color-mix(in oklch, oklch(var(--b2)) 76%, transparent));
    box-shadow:
        inset 0 1px 0 color-mix(in oklch, oklch(var(--b1)) 72%, transparent),
        0 24px 72px color-mix(in oklch, oklch(var(--bc)) 12%, transparent);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
}

.playing-details__art-panel {
    padding: clamp(1rem, 2.4vw, 2rem);
    display: flex;
    flex-direction: column;
}

.playing-details__meta-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.playing-details__label {
    margin: 0;
    font-size: 0.84rem;
    line-height: 1;
    font-weight: 700;
    color: color-mix(in oklch, oklch(var(--bc)) 52%, transparent);
}

.playing-details__title {
    margin: 0.5rem 0 0;
    max-width: 12em;
    font-size: clamp(2rem, 3.6vw, 4.65rem);
    line-height: 0.95;
    font-weight: 850;
    letter-spacing: -0.04em;
    color: oklch(var(--bc));
}

.playing-details__time-pill {
    flex-shrink: 0;
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 9%, transparent);
    border-radius: 999px;
    padding: 0.62rem 0.86rem;
    background: color-mix(in oklch, oklch(var(--b1)) 64%, transparent);
    color: color-mix(in oklch, oklch(var(--bc)) 66%, transparent);
    font-size: 0.78rem;
    font-weight: 700;
    white-space: nowrap;
}

.playing-details__art-stage {
    position: relative;
    flex: 1;
    min-height: 18rem;
    display: flex;
    align-items: center;
    justify-content: center;
    perspective: 1100px;
}

.playing-details__art-glow {
    position: absolute;
    width: min(28rem, 78%);
    aspect-ratio: 1;
    border-radius: 999px;
    background-image: var(--playing-cover);
    background-size: cover;
    background-position: center;
    opacity: 0.32;
    filter: blur(34px) saturate(1.16);
}

.playing-details__record {
    position: absolute;
    width: min(28rem, 78%);
    aspect-ratio: 1;
    border-radius: 999px;
    transform: translateX(12%) rotate(0deg);
    background:
        radial-gradient(circle at center, color-mix(in oklch, oklch(var(--b1)) 88%, oklch(var(--p)) 12%) 0 8%, transparent 8.3%),
        repeating-radial-gradient(circle at center, color-mix(in oklch, oklch(var(--bc)) 20%, transparent) 0 1px, transparent 1px 9px),
        radial-gradient(circle at center, color-mix(in oklch, oklch(var(--bc)) 88%, oklch(var(--b1)) 12%) 0 62%, color-mix(in oklch, oklch(var(--bc)) 70%, oklch(var(--b1)) 30%) 100%);
    box-shadow:
        inset 0 0 0 1px color-mix(in oklch, oklch(var(--b1)) 14%, transparent),
        inset 0 0 34px color-mix(in oklch, oklch(var(--bc)) 58%, transparent),
        0 22px 48px color-mix(in oklch, oklch(var(--bc)) 20%, transparent);
    opacity: 0.78;
    transition: transform 460ms cubic-bezier(0.16, 1, 0.3, 1), opacity 300ms ease, filter 300ms ease;
}

.playing-details__record::before {
    content: "";
    position: absolute;
    inset: 8%;
    border-radius: inherit;
    border: 1px solid color-mix(in oklch, oklch(var(--b1)) 13%, transparent);
    background:
        conic-gradient(from 18deg, transparent 0 38deg, color-mix(in oklch, oklch(var(--b1)) 10%, transparent) 39deg 48deg, transparent 49deg 100deg, color-mix(in oklch, oklch(var(--b1)) 8%, transparent) 101deg 108deg, transparent 109deg 360deg);
    mix-blend-mode: screen;
    opacity: 0.74;
}

.playing-details__record-label {
    position: absolute;
    inset: 38%;
    border-radius: inherit;
    background:
        radial-gradient(circle at 50% 50%, oklch(var(--b1)) 0 12%, transparent 13%),
        linear-gradient(135deg, color-mix(in oklch, oklch(var(--p)) 28%, oklch(var(--b1))), color-mix(in oklch, oklch(var(--bc)) 12%, oklch(var(--b2))));
    box-shadow:
        inset 0 1px 0 color-mix(in oklch, oklch(var(--b1)) 52%, transparent),
        0 0 0 1px color-mix(in oklch, oklch(var(--b1)) 28%, transparent);
}

.playing-details__art-card {
    position: relative;
    width: min(28rem, 78%);
    aspect-ratio: 1;
    border-radius: 1.45rem;
    transform: rotateX(5deg) rotateY(-8deg);
    transition: transform 460ms cubic-bezier(0.16, 1, 0.3, 1), filter 300ms ease;
}

.playing-details__art-card::before,
.playing-details__art-card::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-image: var(--playing-cover);
    background-size: cover;
    background-position: center;
    box-shadow: 0 20px 48px color-mix(in oklch, oklch(var(--bc)) 16%, transparent);
}

.playing-details__art-card::before {
    transform: translate3d(1.1rem, 1.1rem, -3rem) rotate(4deg);
    opacity: 0.26;
}

.playing-details__art-card::after {
    transform: translate3d(0.55rem, 0.55rem, -1.5rem) rotate(2deg);
    opacity: 0.5;
}

.playing-details__cover {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    object-fit: cover;
    box-shadow:
        0 28px 70px color-mix(in oklch, oklch(var(--bc)) 24%, transparent),
        inset 0 1px 0 color-mix(in oklch, oklch(var(--b1)) 32%, transparent);
}

.playing-details__tonearm {
    position: absolute;
    top: 14%;
    right: 15%;
    z-index: 3;
    width: 30%;
    height: 0.45rem;
    border-radius: 999px;
    transform-origin: 88% 50%;
    transform: rotate(-22deg) translateX(1rem);
    background:
        linear-gradient(90deg, color-mix(in oklch, oklch(var(--b1)) 58%, transparent), color-mix(in oklch, oklch(var(--bc)) 52%, oklch(var(--b1)) 48%));
    box-shadow:
        inset 0 1px 0 color-mix(in oklch, oklch(var(--b1)) 42%, transparent),
        0 10px 24px color-mix(in oklch, oklch(var(--bc)) 18%, transparent);
    opacity: 0.52;
    transition: transform 520ms cubic-bezier(0.16, 1, 0.3, 1), opacity 300ms ease;
}

.playing-details__tonearm::before,
.playing-details__tonearm::after {
    content: "";
    position: absolute;
    border-radius: 999px;
}

.playing-details__tonearm::before {
    right: -0.45rem;
    top: 50%;
    width: 1.55rem;
    height: 1.55rem;
    transform: translateY(-50%);
    background: color-mix(in oklch, oklch(var(--bc)) 28%, oklch(var(--b1)) 72%);
    box-shadow: inset 0 1px 0 color-mix(in oklch, oklch(var(--b1)) 46%, transparent);
}

.playing-details__tonearm::after {
    left: -0.3rem;
    bottom: -0.4rem;
    width: 0.7rem;
    height: 0.7rem;
    background: color-mix(in oklch, oklch(var(--p)) 55%, oklch(var(--bc)) 45%);
}

.playing-details__art-stage.is-playing .playing-details__art-glow {
    animation: playing-details-glow-pulse 2.8s ease-in-out infinite;
}

.playing-details__art-stage.is-playing .playing-details__record {
    opacity: 0.92;
    filter: saturate(1.1);
    transform: translateX(18%) rotate(0deg);
    animation: playing-details-record-spin 9s linear infinite;
}

.playing-details__art-stage.is-playing .playing-details__art-card {
    transform: rotateX(5deg) rotateY(-8deg) translate3d(-0.8rem, -0.35rem, 0);
    animation: playing-details-cover-breathe 3.6s ease-in-out infinite;
}

.playing-details__art-stage.is-playing .playing-details__tonearm {
    opacity: 0.88;
    transform: rotate(-7deg) translateX(0);
}

.playing-details__track-card {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.8rem;
}

.playing-details__track-copy {
    min-width: 0;
    border-radius: 1.1rem;
    padding: 0.95rem;
    background: color-mix(in oklch, oklch(var(--b2)) 58%, transparent);
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 7%, transparent);
}

.playing-details__track-copy span,
.playing-details__track-copy strong {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.playing-details__track-copy span {
    font-size: 0.74rem;
    color: color-mix(in oklch, oklch(var(--bc)) 46%, transparent);
}

.playing-details__track-copy strong {
    margin-top: 0.3rem;
    font-size: 0.95rem;
    color: oklch(var(--bc));
}

.playing-details__lyrics-panel {
    padding: clamp(1rem, 2vw, 1.5rem);
    display: flex;
    flex-direction: column;
    gap: 0;
    background: transparent;
    border: 0;
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
}

.playing-details__lyrics-frame {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    border-radius: 0;
    background: transparent;
    border: 0;
}

.playing-details__lyrics-scroll {
    height: 100%;
    overflow-y: auto;
    padding: 0 clamp(0.5rem, 2vw, 1.5rem);
    scrollbar-width: none;
}

.playing-details__lyrics-scroll::-webkit-scrollbar {
    width: 0;
    height: 0;
}

.playing-details__lyrics-spacer {
    height: 34%;
    min-height: 8rem;
}

.playing-details__lyric-line {
    border: 0;
    width: 100%;
    min-height: 3.1rem;
    margin: 0;
    padding: 0.48rem 0.4rem;
    border-radius: 0.95rem;
    display: block;
    background: transparent;
    color: color-mix(in oklch, oklch(var(--bc)) 38%, transparent);
    font-size: clamp(1rem, 1.25vw, 1.2rem);
    line-height: 1.45;
    font-weight: 650;
    text-align: center;
    cursor: pointer;
    transform: translateZ(0);
    transition: color 220ms ease, background-color 220ms ease, transform 220ms ease, opacity 220ms ease;
}

.playing-details__lyric-line:hover {
    background: color-mix(in oklch, oklch(var(--p)) 8%, transparent);
    color: color-mix(in oklch, oklch(var(--bc)) 76%, transparent);
}

.playing-details__lyric-line:active {
    transform: scale(0.99);
}

.playing-details__lyric-line.is-near {
    color: color-mix(in oklch, oklch(var(--bc)) 58%, transparent);
}

.playing-details__lyric-line.is-mid {
    color: color-mix(in oklch, oklch(var(--bc)) 45%, transparent);
}

.playing-details__lyric-line.is-far {
    opacity: 0.64;
}

.playing-details__lyric-line.is-active {
    margin: 0.45rem 0;
    padding: 0.82rem 1rem;
    background: transparent;
    color: color-mix(in oklch, oklch(var(--p)) 74%, oklch(var(--bc)) 26%);
    font-size: clamp(1.55rem, 2.25vw, 2.45rem);
    line-height: 1.22;
    font-weight: 850;
    letter-spacing: -0.035em;
    box-shadow: none;
}

.playing-details__lyrics-fade {
    position: absolute;
    left: 0;
    right: 0;
    height: 8rem;
    z-index: 2;
    pointer-events: none;
}

.playing-details__lyrics-fade--top {
    top: 0;
    background: linear-gradient(180deg, color-mix(in oklch, oklch(var(--b1)) 34%, transparent), transparent);
}

.playing-details__lyrics-fade--bottom {
    bottom: 0;
    background: linear-gradient(0deg, color-mix(in oklch, oklch(var(--b1)) 34%, transparent), transparent);
}

.playing-details__lyrics-loading {
    display: grid;
    gap: 1rem;
    padding: 0 2rem;
}

.playing-details__lyrics-loading span {
    height: 1.35rem;
    border-radius: 999px;
    background: linear-gradient(
        90deg,
        color-mix(in oklch, oklch(var(--bc)) 7%, transparent),
        color-mix(in oklch, oklch(var(--bc)) 13%, transparent),
        color-mix(in oklch, oklch(var(--bc)) 7%, transparent)
    );
    background-size: 200% 100%;
}

.playing-details__lyrics-loading span:nth-child(2n) {
    width: 72%;
    justify-self: center;
}

.playing-details__lyrics-loading span:nth-child(3n) {
    width: 86%;
    justify-self: end;
}

.playing-details__lyrics-empty {
    max-width: 22rem;
    margin: 0 auto;
    padding: 2rem;
    border-radius: 1.2rem;
    text-align: center;
    background: color-mix(in oklch, oklch(var(--b2)) 62%, transparent);
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 8%, transparent);
}

.playing-details__lyrics-empty strong,
.playing-details__lyrics-empty span {
    display: block;
}

.playing-details__lyrics-empty strong {
    color: oklch(var(--bc));
    font-size: 1.1rem;
}

.playing-details__lyrics-empty span {
    margin-top: 0.5rem;
    color: color-mix(in oklch, oklch(var(--bc)) 52%, transparent);
    font-size: 0.9rem;
}

@media (prefers-reduced-motion: no-preference) {
    .playing-details__art-panel,
    .playing-details__lyrics-panel {
        animation: playing-details-enter 480ms cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .playing-details__lyrics-panel {
        animation-delay: 80ms;
    }

    .playing-details__lyrics-loading span {
        animation: playing-details-shimmer 1.2s ease-in-out infinite;
    }
}

@keyframes playing-details-enter {
    from {
        opacity: 0;
        transform: translateY(18px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes playing-details-shimmer {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

@keyframes playing-details-record-spin {
    from {
        transform: translateX(18%) rotate(0deg);
    }
    to {
        transform: translateX(18%) rotate(360deg);
    }
}

@keyframes playing-details-cover-breathe {
    0%,
    100% {
        filter: saturate(1);
    }
    50% {
        filter: saturate(1.08) brightness(1.03);
    }
}

@keyframes playing-details-glow-pulse {
    0%,
    100% {
        opacity: 0.3;
        transform: scale(1);
    }
    50% {
        opacity: 0.43;
        transform: scale(1.04);
    }
}

@media (prefers-reduced-motion: reduce) {
    .playing-details *,
    .playing-details *::before,
    .playing-details *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        scroll-behavior: auto !important;
        transition-duration: 0.01ms !important;
    }
}

@media (max-width: 1024px) {
    .playing-details {
        height: calc(100dvh - 70px);
        min-height: 0;
        overflow-y: auto;
    }

    .playing-details__shell {
        min-height: 100%;
        height: auto;
        grid-template-columns: 1fr;
    }

    .playing-details__art-panel {
        min-height: 36rem;
    }

    .playing-details__lyrics-panel {
        min-height: 34rem;
    }
}

@media (max-width: 768px) {
    .playing-details {
        height: calc(100dvh - 60px);
    }

    .playing-details__shell {
        padding: 0.75rem;
    }

    .playing-details__art-panel,
    .playing-details__lyrics-panel {
        border-radius: 1.3rem;
    }

    .playing-details__meta-top {
        flex-direction: column;
    }

    .playing-details__title {
        font-size: clamp(2rem, 11vw, 3.15rem);
    }

    .playing-details__art-panel {
        min-height: 32rem;
    }

    .playing-details__art-card {
        width: min(18rem, 78vw);
    }

    .playing-details__track-card {
        grid-template-columns: 1fr;
    }

    .playing-details__lyric-line.is-active {
        font-size: 1.65rem;
    }
}

@media (max-width: 640px) {
    .playing-details {
        border-radius: 0;
    }
}
</style>
